import { decodeJwt } from "@/utils/decodeJwt";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router";

type UserClaims = {
    name?: string;
    "https://avak.com.br/roles"?: string[];
    "https://avak.com.br/enterprises"?: {
        enterprises?: string[];
    };
};

export default function SelectApp() {
    const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
    const [user, setUser] = useState<UserClaims | null>(null);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            getAccessTokenSilently()
                .then(token => {
                    setUser(decodeJwt(token));
                })
                .catch(() => {
                    setUser(null);
                });
        } else if (!isLoading && !isAuthenticated) {
            setUser(null);
        }
    }, [isAuthenticated, isLoading, getAccessTokenSilently]);

    if (!isLoading && !isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (isLoading || !user) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-4">
                <div className="rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-lg text-slate-600 font-medium">
                    Carregando...
                </div>
            </div>
        );
    }

    const roles = user["https://avak.com.br/roles"] ?? [];
    const enterprises = user["https://avak.com.br/enterprises"]?.enterprises ?? [];
    const isOwner = roles.includes("Owner");

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
                <div className="absolute bottom-10 -left-24 h-72 w-72 rounded-full bg-violet-100/70 blur-3xl" />
            </div>

            <main className="container-custom relative z-10 min-h-screen py-6 sm:py-12 lg:py-16 flex items-center">
                <section className="mx-auto w-full max-w-4xl rounded-3xl border border-slate-200/70 bg-white/90 p-4 sm:p-8 lg:p-10 shadow-2xl backdrop-blur-sm">
                    <header className="mb-6 sm:mb-8 text-center">
                        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                            Ambiente AVAK
                        </span>
                        <h1 className="mt-4 text-2xl sm:text-4xl font-bold text-slate-900 leading-tight">
                            Escolha o aplicativo para acessar
                        </h1>
                        <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                            Selecione uma opção abaixo para continuar{user.name ? `, ${user.name}` : ""}.
                        </p>
                        <div className="mt-5 flex items-center justify-center gap-3 sm:hidden">
                            <Link
                                to="/"
                                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                            >
                                Home
                            </Link>
                            <button
                                type="button"
                                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                                className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                            >
                                Deslogar
                            </button>
                        </div>
                    </header>

                    <div className="sm:hidden mb-4 grid gap-3">
                        {isOwner ? (
                            <a
                                href="https://gestao.avak.com.br"
                                className="btn-primary w-full text-center"
                            >
                                Ir para a gestão
                            </a>
                        ) : null}

                        {enterprises.length > 0 ? (
                            enterprises.map((item) => (
                                <a
                                    key={item}
                                    href={`https://${item}.avak.com.br/adm`}
                                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                                >
                                    <p className="text-sm font-semibold text-slate-500">Unidade</p>
                                    <p className="mt-1 text-base font-bold text-slate-900">PDV {item}</p>
                                    <p className="mt-2 text-sm font-semibold text-blue-600">Acessar agora</p>
                                </a>
                            ))
                        ) : (
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-600">
                                Nenhuma unidade disponível.
                            </div>
                        )}
                    </div>

                    <div className="hidden sm:block space-y-4 sm:space-y-5">
                        {isOwner ? (
                            <a
                                href="https://gestao.avak.com.br"
                                className="group block rounded-2xl border border-blue-200 bg-blue-50/70 p-4 sm:p-5 transition-all duration-300 hover:border-blue-400 hover:bg-blue-50"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                    <div>
                                        <p className="text-sm font-semibold text-blue-700">Painel administrativo</p>
                                        <p className="mt-1 text-lg sm:text-xl font-bold text-slate-900">Gestão</p>
                                    </div>
                                    <p className="mt-3 text-sm font-semibold text-blue-600 group-hover:text-blue-700">Acessar agora</p>
                                </div>
                            </a>
                        ) : null}

                        <div className="grid gap-4 sm:grid-cols-2">
                            {enterprises.map((item, index) => (
                                <a
                                    key={item}
                                    href={`https://${item}.avak.com.br/adm`}
                                    className={`group rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg ${enterprises.length % 2 === 1 && index === enterprises.length - 1 ? "sm:col-span-2" : ""}`}
                                >
                                    <p className="text-sm font-semibold text-slate-500">Unidade</p>
                                    <p className="mt-1 text-lg sm:text-xl font-bold text-slate-900">PDV {item}</p>
                                    <p className="mt-3 text-sm font-semibold text-blue-600 group-hover:text-blue-700">Acessar agora</p>
                                </a>
                            ))}
                        </div>

                    </div>
                </section>
            </main>
        </div>
    );
}